// utils/formatting.js

import { marked } from 'marked';
import TurndownService from "turndown";
import DOMPurify from "dompurify";


// Convert markdown to HTML
export const renderMarkdownToHtml = (markdown) => {
  // Sanitize to prevent stored XSS — strips <script> and dangerous attributes

  return DOMPurify.sanitize(marked(markdown));
};

// Render markdown to HTML for the editor
export const renderHTML = (text) => {
  return renderMarkdownToHtml(text); // Convert markdown to HTML
};

// Function to convert HTML to plain text
export const convertHtmlToPlainText = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.innerText; // Extracts plain text from HTML
};

// Parse the input date string to a Date object
export const convertDateFormat = (inputDateString) => {
  const date = new Date(inputDateString);

  // Format the date to "Feb 13 2025"
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return {
    date: formattedDate,
    time: formattedTime,
  };
}

export const formatRole = (role) => {
  const [first, second] = role.split("_");

  const formattedFirst =
    first?.toLowerCase() === "hr"
      ? "HR"
      : first
        ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase()
        : "";

  const formattedSecond =
    second?.toLowerCase() === "bp"
      ? "BP"
      : second
        ? second.charAt(0).toUpperCase() + second.slice(1).toLowerCase()
        : "";

  return [formattedFirst, formattedSecond].filter(Boolean).join(" ");
}

export const normalizeCountryName = (name) => {
  if (!name || typeof name !== 'string') {
    return ''; // Return an empty string for invalid input
  }

  const lowerCaseName = name.trim().toLowerCase();

  const usaAliases = ['usa', 'us', 'united states', 'united states of america'];
  const ukAliases = ['uk', 'united kingdom'];

  if (usaAliases.includes(lowerCaseName)) {
    return 'United States'; // Canonical name for USA
  }
  if (ukAliases.includes(lowerCaseName)) {
    return 'United Kingdom'; // Canonical name for UK
  }

  // If no alias matches, return the original name (with standard capitalization for consistency)
  return name.trim();
};


export const markdownToHtml = (text) => {
  marked.setOptions({
    gfm: true,
    breaks: true,
    headerIds: false,
    mangle: false,
  });

  if (!text || typeof text !== "string") return "";

  // Parse markdown but keep inline HTML as-is, then sanitize to prevent stored XSS.
  // DOMPurify removes <script> and dangerous attributes while preserving inline

  return DOMPurify.sanitize(marked.parse(text, { sanitize: false }));
};

export const htmlToMarkdown = (html) => {
  const turndownService = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "*", // use * for unordered lists
    strongDelimiter: "**", // keep bold as **
    emDelimiter: "*",      // italic as *
    br: "  \n",            // <br> → markdown line break
  });

  // --- Preserve inline <span style="..."> exactly ---
  turndownService.addRule("spanStyles", {
    filter: "span",
    replacement: function (content, node) {
      const style = node.getAttribute("style");
      return style ? `<span style="${style}">${content}</span>` : content;
    },
  });

  // --- Preserve paragraphs as double line breaks ---
  turndownService.addRule("paragraphs", {
    filter: "p",
    replacement: function (content) {
      return content.trim() + "\n\n";
    },
  });

  // --- Ensure line breaks survive (<br>) ---
  turndownService.addRule("lineBreak", {
    filter: "br",
    replacement: function () {
      return "  \n";
    },
  });

  // --- Lists: keep markdown bullets/numbers ---
  turndownService.addRule("unorderedList", {
    filter: "ul",
    replacement: function (content) {
      return "\n" + content.trim() + "\n\n";
    },
  });
  turndownService.addRule("listItem", {
    filter: "li",
    replacement: function (content, node, options) {
      const parent = node.parentNode;
      if (parent.nodeName === "OL") {
        const index = Array.prototype.indexOf.call(parent.children, node) + 1;
        return `${index}. ${content}\n`;
      } else {
        return `* ${content}\n`;
      }
    },
  });

  // --- Preserve strong/italic as markdown ---
  turndownService.addRule("strongKeep", {
    filter: "strong",
    replacement: function (content) {
      return `**${content}**`;
    },
  });
  turndownService.addRule("italicKeep", {
    filter: "em",
    replacement: function (content) {
      return `*${content}*`;
    },
  });

  // --- Links [text](url) ---
  turndownService.addRule("links", {
    filter: "a",
    replacement: function (content, node) {
      const href = node.getAttribute("href");
      return href ? `[${content}](${href})` : content;
    },
  });
  if (!html || typeof html !== "string") return "";
  return turndownService.turndown(html);
};

export const buildFieldConfigMap = (orgMetaData, type = null) => {
  // Check for the updated ui_elements key
  if (orgMetaData?.ui_elements && Array.isArray(orgMetaData.ui_elements)) {
    // When a type is provided (e.g. 'req_no', 'job_code', 'create_jd'),
    // only that section's fields are included — avoids cross-section label collisions
    const elements = type
      ? orgMetaData.ui_elements.filter(el => el.type === type)
      : orgMetaData.ui_elements;
    return elements.reduce((acc, element) => {
      if (element?.field_details?.fields && Array.isArray(element.field_details.fields)) {
        element.field_details.fields.forEach((field) => {
          acc[field.field_name] = field;
        });
      }
      return acc;
    }, {});
  }

  if (
    !orgMetaData?.field_configuration?.[0]?.jd_form_fields ||
    !Array.isArray(orgMetaData?.field_configuration?.[0]?.jd_form_fields)
  ) {
    return {};
  }

  return orgMetaData.field_configuration[0].jd_form_fields.reduce((acc, field) => {
    acc[field.field_name] = field;
    return acc;
  }, {});
};

export const getFullNameFromEmail = (email) => {
  if (!email || !email.includes('@')) return '';

  // Extract the part before '@'
  let namePart = email.split('@')[0];

  // Replace common separators with spaces
  namePart = namePart.replace(/[._-]+/g, ' ');

  // Remove digits
  namePart = namePart.replace(/\d+/g, '');

  // Split into tokens and capitalize each
  const nameTokens = namePart
    .split(' ')
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase());

  // Join tokens to form full name
  const fullName = nameTokens.join(' ');

  return fullName || email.split('@')[0];
}

// Check if content is HTML or Markdown
export const isHtmlContent = (content) => {
  if (!content) return false;
  // Check for common HTML tags
  return /<\/?[a-z][\s\S]*>/i.test(content);
};

export const decodeHtmlString = (htmlString) => {
  if (!htmlString) return '';

  let decoded = htmlString
    .replace(/^"/, '')        // remove starting quote
    .replace(/"$/, '')        // remove ending quote
    .replace(/\\"/g, '"')     // unescape quotes
    .replace(/\\n/g, '\n')    // convert \n to actual newlines
    .replace(/\\r/g, '')      // remove \r
    .trim();

  if (isHtmlContent(decoded)) {
    // It's HTML - handle blank lines
    decoded = decoded.replace(/\n{2,}/g, '</p><p>');
    return decoded;
  }
}

export const isMarkdown = (value = "") => {
  if (!value || typeof value !== "string") return false;

  const text = value.trim();

  // Strong Markdown signals (stricter + context-aware)
  const markdownPatterns = [
    /^#{1,6}\s+/m,                         // Headings
    /^>\s+/m,                              // Blockquote
    /^[*-+]\s+/m,                          // Unordered list
    /^\d+\.\s+/m,                          // Ordered list
    /\*\*(?=\S)(.+?)(?<=\S)\*\*/,          // **bold**
    /(?:^|\s)_(?=\S)(.+?)(?<=\S)_(?:\s|$)/, // _italic_ (not inside URLs)
    /```[\s\S]*?```/,                      // Code blocks
    /\[(.+?)\]\((https?:\/\/|\/)[^)]+\)/,  // Links
    /!\[(.*?)\]\((https?:\/\/|\/)[^)]+\)/  // Images
  ];

  // Markdown signals win over inline HTML — marked() handles <u>/<span>/etc. fine
  // when mixed with markdown. Without this, JDs containing inline HTML tags fall
  // through to the raw-HTML renderer and markdown syntax shows as literal text.
  if (markdownPatterns.some((re) => re.test(text))) return true;

  // No markdown signals: legacy pure-HTML JDs still route to the HTML renderer.
  const htmlTagPattern = /<\/?[a-z][\s\S]*?>/i;
  return !htmlTagPattern.test(text);
};

export const generateSurgicalId = (length = 15) => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export const calculateNextVersion = (currentVer) => {
  const s = String(currentVer);
  if (!s.includes(".")) return parseFloat(s + ".1");
  const decimals = s.split(".")[1].length;
  const step = Math.pow(10, -decimals);
  const mathCheck = parseFloat((currentVer + step).toFixed(decimals));
  if (Math.floor(mathCheck) > Math.floor(currentVer)) {
    return parseFloat(s + "1");
  } else {
    return mathCheck;
  }
};

